import { useState } from "react";
import { X, Lock, Unlock, RotateCcw, Check } from "lucide-react";
import { cn } from "@nthamizh/ui";
import { useToast } from "@nthamizh/ui";
import { Badge } from "./Badge";

interface UserRole { roleId: string; roleName: string; roleKey: string; moduleKey: string | null }
interface AvailableRole { id: string; name: string; key: string; moduleKey: string | null }

interface UserProfile {
  id: string;
  name: string;
  email: string;
  jobTitle: string | null;
  isLocked: boolean;
  failedLoginAttempts: number;
  status: "active" | "pending" | "inactive";
  memberSince: string;
  roles: UserRole[];
}

interface UserDrawerProps {
  user: UserProfile | null;
  availableRoles: AvailableRole[];
  isOpen: boolean;
  onClose: () => void;
  onUnlock?: (userId: string) => Promise<void>;
  onResetPassword?: (userId: string) => Promise<{ tempPassword: string }>;
  onAssignRoles?: (userId: string, roleIds: string[]) => Promise<void>;
  onApprove?: (userId: string) => Promise<void>;
  readOnly?: boolean;
}

export function UserDrawer({
  user, availableRoles, isOpen, onClose,
  onUnlock, onResetPassword, onAssignRoles, onApprove, readOnly = false,
}: UserDrawerProps) {
  const { toast } = useToast();
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [showRoleEditor, setShowRoleEditor] = useState(false);
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [loading, setLoading] = useState<string | null>(null);

  if (!isOpen || !user) return null;

  const openRoleEditor = () => {
    setSelectedRoles(user.roles.map((r) => r.roleId));
    setShowRoleEditor(true);
  };

  const handleUnlock = async () => {
    if (!onUnlock) return;
    setLoading("unlock");
    try {
      await onUnlock(user.id);
      toast({ title: `${user.name} unlocked`, variant: "success" });
    } catch {
      toast({ title: "Failed to unlock user", variant: "error" });
    } finally { setLoading(null); }
  };

  const handleResetPw = async () => {
    if (!onResetPassword) return;
    setLoading("reset");
    try {
      const result = await onResetPassword(user.id);
      setTempPassword(result.tempPassword);
      toast({ title: "Password reset", variant: "success" });
    } catch {
      toast({ title: "Failed to reset password", variant: "error" });
    } finally { setLoading(null); }
  };

  const handleSaveRoles = async () => {
    if (!onAssignRoles) return;
    setLoading("roles");
    try {
      await onAssignRoles(user.id, selectedRoles);
      setShowRoleEditor(false);
      toast({ title: "Roles updated", variant: "success" });
    } catch {
      toast({ title: "Failed to update roles", variant: "error" });
    } finally { setLoading(null); }
  };

  const handleApprove = async () => {
    if (!onApprove) return;
    setLoading("approve");
    try {
      await onApprove(user.id);
      toast({ title: `${user.name} approved`, variant: "success" });
      onClose();
    } catch {
      toast({ title: "Failed to approve user", variant: "error" });
    } finally { setLoading(null); }
  };

  const toggleRole = (id: string) =>
    setSelectedRoles((prev) => prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]);

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />

      {/* Drawer */}
      <div className="fixed inset-y-0 right-0 w-96 bg-white shadow-2xl z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-ds-border">
          <h2 className="text-sm font-semibold text-ds-text">User profile</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-ds-bg text-ds-text-muted">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Identity */}
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 text-base font-semibold flex-shrink-0">
              {user.name.slice(0, 2).toUpperCase()}
            </div>
            <div>
              <p className="text-sm font-semibold text-ds-text">{user.name}</p>
              <p className="text-xs text-ds-text-muted">{user.email}</p>
              {user.jobTitle && <p className="text-xs text-ds-text-muted mt-0.5">{user.jobTitle}</p>}
            </div>
          </div>

          {/* Status row */}
          <div className="flex flex-wrap gap-2 items-center">
            {user.status === "active" && <Badge variant="green">Active</Badge>}
            {user.status === "pending" && <Badge variant="amber">Pending approval</Badge>}
            {user.status === "inactive" && <Badge variant="gray">Inactive</Badge>}
            {user.isLocked && <Badge variant="red"><Lock className="w-2.5 h-2.5 mr-1" />Locked ({user.failedLoginAttempts} failed attempts)</Badge>}
          </div>

          {/* Info rows */}
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-ds-text-secondary">Member since</span>
              <span className="text-ds-text">{new Date(user.memberSince).toLocaleDateString()}</span>
            </div>
          </div>

          {/* Roles */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-ds-text-muted uppercase tracking-wide">Roles</p>
              {!readOnly && onAssignRoles && (
                <button onClick={openRoleEditor} className="text-xs text-indigo-600 hover:underline">
                  {showRoleEditor ? "Cancel" : "Edit roles"}
                </button>
              )}
            </div>

            {showRoleEditor ? (
              <div className="space-y-1.5">
                {availableRoles.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => toggleRole(r.id)}
                    className={cn(
                      "w-full flex items-center gap-2.5 px-3 py-2 rounded-lg border text-left text-xs transition-colors",
                      selectedRoles.includes(r.id) ? "border-indigo-300 bg-indigo-50" : "border-ds-border hover:bg-ds-bg"
                    )}
                  >
                    <div className={cn("w-4 h-4 rounded border flex items-center justify-center flex-shrink-0",
                      selectedRoles.includes(r.id) ? "bg-indigo-600 border-indigo-600" : "border-ds-border-strong")}>
                      {selectedRoles.includes(r.id) && <Check className="w-2.5 h-2.5 text-white" />}
                    </div>
                    <div>
                      <p className="font-medium text-ds-text">{r.name}</p>
                      {r.moduleKey && <p className="text-ds-text-muted capitalize">{r.moduleKey} module</p>}
                    </div>
                  </button>
                ))}
                <button
                  onClick={handleSaveRoles}
                  disabled={loading === "roles"}
                  className="w-full py-2 rounded-lg bg-indigo-600 text-white text-xs font-medium hover:bg-indigo-700 disabled:opacity-50 mt-2"
                >
                  {loading === "roles" ? "Saving..." : "Save roles"}
                </button>
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {user.roles.length > 0
                  ? user.roles.map((r) => <Badge key={r.roleId} variant="blue">{r.roleName}</Badge>)
                  : <span className="text-xs text-ds-text-muted">No roles assigned</span>}
              </div>
            )}
          </div>

          {/* Temp password display */}
          {tempPassword && (
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
              <p className="text-xs font-semibold text-amber-700 mb-1">Temporary password</p>
              <p className="text-sm font-mono text-amber-900 break-all">{tempPassword}</p>
              <p className="text-xs text-amber-600 mt-1">Share this securely with the user. They must change it on first login.</p>
            </div>
          )}
        </div>

        {/* Actions footer */}
        {!readOnly && (
          <div className="px-5 py-4 border-t border-ds-border space-y-2">
            {user.status === "pending" && onApprove && (
              <button
                onClick={handleApprove}
                disabled={loading === "approve"}
                className="w-full py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
              >
                {loading === "approve" ? "Approving..." : "Approve user"}
              </button>
            )}
            {user.isLocked && onUnlock && (
              <button
                onClick={handleUnlock}
                disabled={loading === "unlock"}
                className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg bg-amber-50 text-amber-700 border border-amber-200 text-sm font-medium hover:bg-amber-100 disabled:opacity-50"
              >
                <Unlock className="w-3.5 h-3.5" />
                {loading === "unlock" ? "Unlocking..." : "Unlock account"}
              </button>
            )}
            {onResetPassword && (
              <button
                onClick={handleResetPw}
                disabled={loading === "reset"}
                className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 disabled:opacity-50"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                {loading === "reset" ? "Resetting..." : "Reset password"}
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
}
